import React, { useContext, useMemo } from "react";
import zip from "lodash/zip";
import uniq from "lodash/uniq";
import flattenDeep from "lodash/flattenDeep";
import MessageFavoriteFieldsContext from "views/components/contexts/MessageFavoriteFieldsContext";
import StreamsContext from "contexts/StreamsContext";
import { isPermitted } from "util/PermissionsMixin";
import useCurrentUser from "hooks/useCurrentUser";
import { getStreamFavoriteFields } from "components/common/message/helpers";
import useMessageFavoriteFieldsMutation from "components/common/message/details/fields/hooks/useMessageFavoriteFieldsMutation";
const OriginalMessageFavoriteFieldsProvider = ({ children = null, message, messageFields }) => {
  var _a;
  const streamsContext = useContext(StreamsContext);
  const streamsList = useMemo(() => streamsContext != null ? streamsContext : [], [streamsContext]);
  const { permissions } = useCurrentUser();
  const streams = useMemo(() => {
    var _a2, _b;
    const messageStreamIds = (_b = (_a2 = message == null ? void 0 : message.fields) == null ? void 0 : _a2.streams) != null ? _b : [];
    const streamsById = Object.fromEntries(
      streamsList.filter((stream) => isPermitted(permissions, `streams:read:${stream.id}`)).map((stream) => [stream.id, stream])
    );
    return messageStreamIds.map((id) => streamsById == null ? void 0 : streamsById[id]).filter((s) => !!s);
  }, [(_a = message == null ? void 0 : message.fields) == null ? void 0 : _a.streams, permissions, streamsList]);
  const initialFavoriteFieldsByStream = useMemo(
    () => Object.fromEntries(streams.map((stream) => [stream.id, getStreamFavoriteFields(stream, message == null ? void 0 : message.fields)])),
    [message == null ? void 0 : message.fields, streams]
  );
  const initialFavoriteFields = useMemo(
    () => uniq(flattenDeep(zip(Object.values(initialFavoriteFieldsByStream)))),
    [initialFavoriteFieldsByStream]
  );
  const editableStreams = useMemo(
    () => streams.filter((stream) => isPermitted(permissions, `streams:edit:${stream.id}`)),
    [permissions, streams]
  );
  const editableStreamsInitialFavoriteFields = useMemo(
    () => Object.fromEntries(editableStreams.map(({ id }) => [id, initialFavoriteFieldsByStream[id]])),
    [editableStreams, initialFavoriteFieldsByStream]
  );
  const { saveFavoriteField, toggleField, setFieldsIsPending } = useMessageFavoriteFieldsMutation(
    editableStreamsInitialFavoriteFields,
    initialFavoriteFields
  );
  const contextValue = useMemo(
    () => ({
      favoriteFields: initialFavoriteFields,
      saveFavoriteField,
      messageFields,
      message,
      toggleField,
      editableStreams,
      setFieldsIsPending,
      initialFavoriteFieldsByStream
    }),
    [
      initialFavoriteFields,
      saveFavoriteField,
      messageFields,
      message,
      toggleField,
      setFieldsIsPending,
      initialFavoriteFieldsByStream,
      editableStreams
    ]
  );
  return /* @__PURE__ */ React.createElement(MessageFavoriteFieldsContext.Provider, { value: contextValue }, children);
};
const MessageFavoriteFieldsProvider = ({
  children = null,
  message,
  messageFields,
  isFeatureEnabled
}) => {
  if (!isFeatureEnabled) return children;
  return /* @__PURE__ */ React.createElement(OriginalMessageFavoriteFieldsProvider, { message, messageFields }, children);
};
export default MessageFavoriteFieldsProvider;
